import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/format";
import { AlertTriangle, ArrowUpRight, BarChart3, Bell, CheckCheck, CircleHelp, Filter, Info, MessageSquare, ClipboardCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const KIND_CONFIG = {
    alert: {
        label: "Alert",
        icon: AlertTriangle,
        dot: "bg-red-500",
        badge: "border-red-500/25 bg-red-500/10 text-red-700 dark:text-red-300",
        iconTone: "bg-red-500/10 text-red-600 dark:text-red-300",
    },
    task: {
        label: "Task",
        icon: ClipboardCheck,
        dot: "bg-amber-500",
        badge: "border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-300",
        iconTone: "bg-amber-500/10 text-amber-600 dark:text-amber-300",
    },
    comm: {
        label: "Communication",
        icon: MessageSquare,
        dot: "bg-blue-500",
        badge: "border-blue-500/25 bg-blue-500/10 text-blue-700 dark:text-blue-300",
        iconTone: "bg-blue-500/10 text-blue-600 dark:text-blue-300",
    },
    analytics_recommendation: {
        label: "Analytics",
        icon: BarChart3,
        dot: "bg-violet-500",
        badge: "border-violet-500/25 bg-violet-500/10 text-violet-700 dark:text-violet-300",
        iconTone: "bg-violet-500/10 text-violet-600 dark:text-violet-300",
    },
    info: {
        label: "Info",
        icon: Info,
        dot: "bg-primary",
        badge: "border-primary/25 bg-primary/10 text-primary",
        iconTone: "bg-primary/10 text-primary",
    },
};

const FILTER_OPTIONS = [
    { value: "all", label: "All types" },
    { value: "alert", label: "Alerts" },
    { value: "task", label: "Tasks" },
    { value: "comm", label: "Communication" },
    { value: "analytics_recommendation", label: "Analytics" },
    { value: "info", label: "Info" },
];

function notificationConfig(kind) {
    return KIND_CONFIG[kind] || { ...KIND_CONFIG.info, label: kind ? "Update" : "Info", icon: CircleHelp };
}

function notificationLink(notification) {
    const explicitLink = notification.link || notification.target_url;
    if (typeof explicitLink === "string" && explicitLink.startsWith("/") && !explicitLink.startsWith("//")) return explicitLink;
    const entityRoutes = { booking: "/bookings", client: "/clients", invoice: "/invoices", project: "/projects" };
    if (notification.entity_type && notification.entity_id && entityRoutes[notification.entity_type]) return `${entityRoutes[notification.entity_type]}/${notification.entity_id}`;
    if (notification.kind === "analytics_recommendation") return "/analytics/recommendations";
    const title = String(notification.title || "").toLowerCase();
    if (title.includes("inquiry") || title.includes("client")) return "/crm";
    if (title.includes("session") || title.includes("booking") || title.includes("arrival")) return "/calendar";
    if (title.includes("invoice") || title.includes("deposit") || title.includes("payment")) return "/invoices";
    if (title.includes("follow-up") || notification.kind === "task") return "/dashboard";
    return "/dashboard";
}

export default function InboxPage() {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [kindFilter, setKindFilter] = useState("all");
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [openingId, setOpeningId] = useState(null);
    const { data: notifs = [] } = useQuery({ queryKey: ["notifications"], queryFn: () => apiGet("/notifications") });
    const markRead = async (id) => { await apiPost(`/notifications/${id}/read`); qc.invalidateQueries({ queryKey: ["notifications"] }); };
    const openNotification = async (notification) => {
        if (openingId) return;
        setOpeningId(notification.id);
        try {
            if (!notification.read) {
                await apiPost(`/notifications/${notification.id}/read`);
                qc.setQueryData(["notifications"], (current = []) => current.map((item) => item.id === notification.id ? { ...item, read: true } : item));
            }
            navigate(notificationLink(notification));
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not open this notification");
        } finally {
            setOpeningId(null);
        }
    };
    const unreadCount = notifs.filter((notification) => !notification.read).length;
    const filteredNotifs = useMemo(() => notifs.filter((notification) => {
        const matchesKind = kindFilter === "all" || notification.kind === kindFilter;
        const matchesReadState = !unreadOnly || !notification.read;
        return matchesKind && matchesReadState;
    }), [kindFilter, notifs, unreadOnly]);
    const hasFilters = kindFilter !== "all" || unreadOnly;

    return (
        <div className="w-full px-4 py-6 sm:px-6 lg:px-8" data-testid="inbox-page">
            <PageHeader title="Inbox" subtitle="Stay on top of studio activity, follow-ups, and important changes." testId="inbox-header" />
            <Card className="border-border p-3 sm:p-4" data-testid="notification-filters">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium"><Filter className="h-4 w-4 text-primary" />Filter notifications</div>
                    <div className="flex flex-wrap items-center gap-2">
                        <label className="sr-only" htmlFor="notification-kind-filter">Notification type</label>
                        <select id="notification-kind-filter" value={kindFilter} onChange={(event) => setKindFilter(event.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="notification-kind-filter">
                            {FILTER_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                        <Button type="button" size="sm" variant={unreadOnly ? "default" : "outline"} onClick={() => setUnreadOnly((current) => !current)} data-testid="notification-unread-filter">
                            <Bell className="mr-1.5 h-3.5 w-3.5" />Unread{unreadCount > 0 ? ` (${unreadCount})` : ""}
                        </Button>
                        {hasFilters && <Button type="button" size="sm" variant="ghost" onClick={() => { setKindFilter("all"); setUnreadOnly(false); }} data-testid="notification-clear-filters">Clear</Button>}
                    </div>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">Showing {filteredNotifs.length} of {notifs.length} notifications</div>
            </Card>
            <Card className="mt-4 divide-y divide-border border-border" data-testid="notification-list">
                {filteredNotifs.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">{notifs.length === 0 ? "Nothing new." : "No notifications match these filters."}</div>}
                {filteredNotifs.map((notification) => {
                    const config = notificationConfig(notification.kind);
                    const Icon = config.icon;
                    return <div key={notification.id} className={`flex items-start gap-3 p-4 transition-colors hover:bg-muted/30 ${notification.read ? "opacity-60" : ""}`} data-testid={`notification-${notification.id}`}>
                        <button type="button" onClick={() => openNotification(notification)} disabled={openingId === notification.id} className="group flex min-w-0 flex-1 items-start gap-3 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-wait" aria-label={`Open notification: ${notification.title}`} data-testid={`notification-open-${notification.id}`}>
                            <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${config.iconTone}`}><Icon className="h-4 w-4" strokeWidth={1.8} /></div>
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2"><span className={`h-2 w-2 rounded-full ${config.dot}`} aria-hidden="true" /><div className="font-medium group-hover:text-primary">{notification.title}</div><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${config.badge}`}>{config.label}</span>{!notification.read && <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-primary">Unread</span>}</div>
                                {notification.body && <div className="mt-1 text-sm text-muted-foreground">{notification.body}</div>}
                                <div className="mt-1 text-xs text-muted-foreground">{fmtDateTime(notification.created_at)}</div>
                            </div>
                            <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" aria-hidden="true" />
                        </button>
                        {!notification.read && <Button variant="ghost" size="sm" onClick={() => markRead(notification.id)} data-testid={`notification-mark-read-${notification.id}`}><CheckCheck className="mr-1.5 h-4 w-4" />Mark read</Button>}
                    </div>;
                })}
            </Card>
        </div>
    );
}
